import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { ensureEventId } from './useEvent';
import { toast } from '@/hooks/use-toast';

export interface BudgetCategory {
  id: string;
  user_id: string;
  name: string;
  icon: string;
  color: string;
  allocated_amount: number;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export interface BudgetExpense {
  id: string;
  user_id: string;
  category_id: string;
  description: string;
  amount: number;
  estimated_amount: number;
  vendor_name: string | null;
  paid_date: string | null;
  is_paid: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface BudgetExpensePayment {
  id: string;
  expense_id: string;
  amount: number;
  payment_date: string;
  notes: string | null;
  created_at: string;
}

export interface WeddingSettings {
  id: string;
  user_id: string;
  total_budget: number;
  wedding_date: string | null;
  bride_name: string | null;
  groom_name: string | null;
}

// Fetch wedding settings — 006: la fuente única es la entidad Evento (`weddings`).
// Se mantiene la forma `WeddingSettings` para no romper consumidores existentes.
export const useWeddingSettings = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['wedding-settings', user?.id],
    queryFn: async () => {
      if (!user) return null;

      const { data, error } = await supabase
        .from('weddings')
        .select('id, user_id, total_budget, wedding_date, bride_name, groom_name')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;
      if (!data) return null;
      return {
        id: data.id,
        user_id: data.user_id,
        total_budget: data.total_budget ?? 0,
        wedding_date: data.wedding_date,
        bride_name: data.bride_name,
        groom_name: data.groom_name,
      } as WeddingSettings;
    },
    enabled: !!user,
  });
};

// Update/Create wedding settings — 006: escribe en la entidad Evento (`weddings`).
export const useUpdateWeddingSettings = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (settings: Partial<WeddingSettings>) => {
      if (!user) throw new Error('No user');

      const patch = {
        total_budget: settings.total_budget,
        wedding_date: settings.wedding_date,
        bride_name: settings.bride_name,
        groom_name: settings.groom_name,
      };

      const { data: existing } = await supabase
        .from('weddings')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (existing) {
        const { data, error } = await supabase
          .from('weddings')
          .update(patch)
          .eq('user_id', user.id)
          .select()
          .single();
        if (error) throw error;
        return data;
      } else {
        const { data, error } = await supabase
          .from('weddings')
          .insert({ ...patch, user_id: user.id })
          .select()
          .single();
        if (error) throw error;
        return data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wedding-settings'] });
      queryClient.invalidateQueries({ queryKey: ['event'] });
      toast({ title: 'Presupuesto actualizado' });
    },
    onError: (error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });
};

// Fetch categories
export const useBudgetCategories = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['budget-categories', user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data, error } = await supabase
        .from('budget_categories')
        .select('*')
        .eq('user_id', user.id)
        .order('display_order', { ascending: true });

      if (error) throw error;
      return data as BudgetCategory[];
    },
    enabled: !!user,
  });
};

// Create category
export const useCreateCategory = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (category: { name: string; color: string; allocated_amount: number }) => {
      if (!user) throw new Error('No user');

      const eventId = await ensureEventId(user.id);
      const { data, error } = await supabase
        .from('budget_categories')
        .insert({
          name: category.name,
          color: category.color,
          allocated_amount: category.allocated_amount,
          user_id: user.id,
          event_id: eventId,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['budget-categories'] });
      toast({ title: 'Categoría creada' });
    },
    onError: (error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });
};

// Update category
export const useUpdateCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<BudgetCategory> & { id: string }) => {
      const { data, error } = await supabase
        .from('budget_categories')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['budget-categories'] });
      toast({ title: 'Categoría actualizada' });
    },
    onError: (error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });
};

// Delete category
export const useDeleteCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('budget_categories')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['budget-categories'] });
      queryClient.invalidateQueries({ queryKey: ['budget-expenses'] });
      toast({ title: 'Categoría eliminada' });
    },
    onError: (error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });
};

// Fetch expenses
export const useBudgetExpenses = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['budget-expenses', user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data, error } = await supabase
        .from('budget_expenses')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as BudgetExpense[];
    },
    enabled: !!user,
  });
};

// Create expense
export const useCreateExpense = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (expense: {
      category_id: string;
      description: string;
      amount: number;
      estimated_amount: number;
      vendor_name: string | null;
      is_paid: boolean;
      paid_date: string | null;
    }) => {
      if (!user) throw new Error('No user');

      const eventId = await ensureEventId(user.id);
      const { data, error } = await supabase
        .from('budget_expenses')
        .insert({
          category_id: expense.category_id,
          description: expense.description,
          amount: expense.amount,
          estimated_amount: expense.estimated_amount,
          vendor_name: expense.vendor_name,
          is_paid: expense.is_paid,
          paid_date: expense.paid_date,
          user_id: user.id,
          event_id: eventId,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['budget-expenses'] });
      toast({ title: 'Gasto registrado' });
    },
    onError: (error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });
};

// Update expense
export const useUpdateExpense = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<BudgetExpense> & { id: string }) => {
      const { data, error } = await supabase
        .from('budget_expenses')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['budget-expenses'] });
      toast({ title: 'Gasto actualizado' });
    },
    onError: (error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });
};

// Delete expense
export const useDeleteExpense = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('budget_expenses')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['budget-expenses'] });
      toast({ title: 'Gasto eliminado' });
    },
    onError: (error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });
};

// Fetch payments for an expense
export const useExpensePayments = (expenseId?: string) => {
  return useQuery({
    queryKey: ['expense-payments', expenseId],
    queryFn: async () => {
      if (!expenseId) return [];

      const { data, error } = await supabase
        .from('budget_expense_payments')
        .select('*')
        .eq('expense_id', expenseId)
        .order('payment_date', { ascending: false });

      if (error) throw error;
      return data as BudgetExpensePayment[];
    },
    enabled: !!expenseId,
  });
};

// Fetch all payments for user
export const useAllExpensePayments = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['all-expense-payments', user?.id],
    queryFn: async () => {
      if (!user) return [];

      // First get all user's expenses
      const { data: expenses, error: expensesError } = await supabase
        .from('budget_expenses')
        .select('id')
        .eq('user_id', user.id);

      if (expensesError) throw expensesError;

      const expenseIds = expenses?.map(e => e.id) || [];
      if (expenseIds.length === 0) return [];

      // Then get all payments for those expenses
      const { data, error } = await supabase
        .from('budget_expense_payments')
        .select('*')
        .in('expense_id', expenseIds);

      if (error) throw error;
      return data as BudgetExpensePayment[];
    },
    enabled: !!user,
  });
};

// Create payment
export const useCreateExpensePayment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payment: {
      expense_id: string;
      amount: number;
      payment_date: string;
      notes?: string;
    }) => {
      const { data, error } = await supabase
        .from('budget_expense_payments')
        .insert(payment)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['expense-payments', variables.expense_id] });
      queryClient.invalidateQueries({ queryKey: ['all-expense-payments'] });
      toast({ title: 'Pago registrado' });
    },
    onError: (error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });
};

// Delete payment
export const useDeleteExpensePayment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase
        .from('budget_expense_payments')
        .delete()
        .eq('id', id)
        .select('expense_id')
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      if (data) {
        queryClient.invalidateQueries({ queryKey: ['expense-payments', data.expense_id] });
        queryClient.invalidateQueries({ queryKey: ['all-expense-payments'] });
      }
      toast({ title: 'Pago eliminado' });
    },
    onError: (error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });
};
